/**
 * Shared building blocks for every tool page: the page chrome, the file input
 * (drop zone → file chip), the result panel's status line, the "what to fix"
 * list, disclosures, and the quality picker. Each tool composes these so the
 * three tools read as one product and a fix lands everywhere at once.
 *
 * Everything here returns HTML strings for innerHTML templates, so any value
 * that came from a file (names, messages) must go through `escapeHtml`.
 */
import { renderTopBar, type ToolId } from './top-bar';

export type Status = 'green' | 'yellow' | 'red';

/**
 * HTML-entity-escape a string for safe interpolation into innerHTML templates.
 * Note: don't use the global `escape()` — that's the legacy URI encoder, not
 * an HTML escaper, and would let `<` / `>` through unchanged.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ─── Icons (inline, stroke = currentColor) ─────────────────────────────────

const svg = (size: number, body: string): string =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

export const ICONS = {
  upload: (s = 28) => svg(s, '<path d="M12 16V4M7 9l5-5 5 5"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>'),
  file: (s = 20) => svg(s, '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/>'),
  lock: (s = 14) => svg(s, '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>'),
  check: (s = 18) => svg(s, '<circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/>'),
  download: (s = 16) => svg(s, '<path d="M12 4v11M7 10l5 5 5-5"/><path d="M5 20h14"/>'),
  cube: (s = 24) => svg(s, '<path d="M12 2 3 7v10l9 5 9-5V7z"/><path d="m3 7 9 5 9-5M12 12v10"/>'),
  film: (s = 24) => svg(s, '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4"/>'),
  target: (s = 24) => svg(s, '<rect x="4" y="4" width="16" height="16" rx="1"/><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"/><circle cx="12" cy="12" r="2.5"/>'),
};

// ─── Page chrome ───────────────────────────────────────────────────────────

/** Top bar + the tool's title, one-line purpose, and the privacy promise. */
export function renderPageHeader(tool: ToolId, title: string, lede: string, privacy = 'Files stay on your device — nothing is uploaded.'): string {
  return `
    ${renderTopBar(tool)}
    <header class="intro">
      <h1>${title}</h1>
      <p>${lede}</p>
      <div class="privacy-note">${ICONS.lock()}${privacy}</div>
    </header>
  `;
}

// ─── File input ────────────────────────────────────────────────────────────

export interface FileInputOptions {
  accept: string;
  /** Bold first line, e.g. "Drop a GLB or glTF file". */
  prompt: string;
  /** Muted second line: formats and limits. */
  hint: string;
}

/**
 * The big empty-state target. A <label> wrapping a transparent, full-size file
 * input: clicking anywhere opens the picker natively, and the input stays in
 * the tab order for keyboard users. Drops are handled by `wireFileDrop`.
 */
export function renderDropZone(opts: FileInputOptions): string {
  return `
    <label class="drop-zone">
      <input class="file-input" type="file" accept="${opts.accept}" />
      <span class="drop-icon">${ICONS.upload()}</span>
      <strong>${opts.prompt}</strong>
      <span class="hint">or click to choose one. ${opts.hint}</span>
    </label>
  `;
}

/**
 * The loaded-file row: name, size (plus optional extra meta line), and a
 * "Choose another" control that is itself a file picker. The whole page still
 * accepts drops to swap the file.
 */
export function renderFileChip(file: File, accept: string, extraMeta = ''): string {
  return `
    <div class="file-chip">
      <span class="file-chip-icon">${ICONS.file()}</span>
      <div class="file-chip-text">
        <div class="file-chip-name" title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</div>
        <div class="file-chip-meta">${formatFileSize(file.size)}${extraMeta ? `, ${extraMeta}` : ''}</div>
      </div>
      <label class="btn btn-small">
        <input class="file-input" type="file" accept="${accept}" />
        Choose another
      </label>
    </div>
  `;
}

/**
 * Wire file selection for a tool page: the picker's change event, and drag &
 * drop anywhere on the page (not just on the drop zone — once a file is loaded
 * the drop zone is gone, and swapping files should stay one gesture).
 */
export function wireFileInput(root: HTMLElement, onFile: (file: File) => void): void {
  root.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if (!target.matches('input.file-input')) return;
    const input = target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so choosing the same file again still fires `change`.
    input.value = '';
    if (file) onFile(file);
  });

  const hasFiles = (e: DragEvent): boolean => !!e.dataTransfer?.types.includes('Files');
  let depth = 0;
  document.addEventListener('dragenter', (e) => {
    if (!hasFiles(e)) return;
    depth++;
    document.body.classList.add('dragging');
  });
  document.addEventListener('dragleave', (e) => {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0) document.body.classList.remove('dragging');
  });
  document.addEventListener('dragover', (e) => {
    if (hasFiles(e)) e.preventDefault();
  });
  document.addEventListener('drop', (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth = 0;
    document.body.classList.remove('dragging');
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  });
}

// ─── Result panel pieces ───────────────────────────────────────────────────

/** Status light + headline + one supporting sentence. `busy` pulses the light. */
export function renderStatusHead(status: Status | 'busy', title: string, sub = ''): string {
  return `
    <div class="status-head">
      <span class="status-light ${status}" aria-hidden="true"></span>
      <div>
        <h2 class="status-title">${escapeHtml(title)}</h2>
        ${sub ? `<p class="status-sub">${sub}</p>` : ''}
      </div>
    </div>
  `;
}

/** A labelled progress bar (ratio 0–1). */
export function renderProgress(label: string, ratio: number): string {
  const pct = Math.max(0, Math.min(1, ratio)) * 100;
  return `
    <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}">
      <div class="progress-label"><span>${escapeHtml(label)}</span><span>${Math.round(pct)}%</span></div>
      <div class="progress-track"><div class="progress-bar" style="width:${pct.toFixed(1)}%"></div></div>
    </div>
  `;
}

/**
 * Before → after file size, drawn as a bar that shrinks. `estimate` hatches
 * the bar and prefixes "≈" for a predicted (not yet produced) output.
 */
export function renderSizeMeter(fromBytes: number, toBytes: number, opts: { estimate?: boolean; note?: string } = {}): string {
  const ratio = fromBytes > 0 ? toBytes / fromBytes : 1;
  const smaller = toBytes <= fromBytes;
  const pct = Math.round(Math.abs(1 - ratio) * 100);
  const delta = pct === 0
    ? `<span class="meter-delta">No change</span>`
    : `<span class="meter-delta ${smaller ? 'smaller' : 'larger'}">${pct}% ${smaller ? 'smaller' : 'larger'}</span>`;
  const width = Math.max(1.5, Math.min(100, ratio * 100));
  return `
    <div class="meter">
      <div class="meter-numbers">
        <span class="meter-from">${formatFileSize(fromBytes)}</span>
        <span class="meter-arrow" aria-label="to">→</span>
        <span class="meter-to">${opts.estimate ? '≈ ' : ''}${formatFileSize(toBytes)}</span>
        ${delta}
      </div>
      <div class="meter-track" aria-hidden="true"><div class="meter-fill${opts.estimate ? ' estimate' : ''}" style="width:${width.toFixed(1)}%"></div></div>
      ${opts.note ? `<div class="meter-note">${opts.note}</div>` : ''}
    </div>
  `;
}

/** Short labelled facts (resolution, duration…) shown under the status. */
export function renderFacts(facts: Array<[string, string]>): string {
  if (facts.length === 0) return '';
  return `<dl class="facts">${facts.map(([k, v]) => `<div><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd></div>`).join('')}</dl>`;
}

// ─── Quality picker ────────────────────────────────────────────────────────

/**
 * The same three words across every tool, so "Balanced" means the same kind of
 * trade-off on a model and on a video. Keyed by the library's profile ids.
 */
export interface QualityOption {
  id: string;
  label: string;
  hint: string;
}

export function renderQualityPicker(options: QualityOption[], selectedId: string, disabled = false): string {
  const selected = options.find(o => o.id === selectedId);
  const segs = options.map(o => `
    <label>
      <input type="radio" name="quality" value="${o.id}" ${o.id === selectedId ? 'checked' : ''} ${disabled ? 'disabled' : ''} />
      <span>${escapeHtml(o.label)}</span>
    </label>`).join('');
  return `
    <div class="quality">
      <div class="quality-row">
        <span class="quality-label" id="quality-label">Quality</span>
        <div class="segmented" role="radiogroup" aria-labelledby="quality-label">${segs}</div>
      </div>
      ${selected ? `<div class="quality-hint">${escapeHtml(selected.hint)}</div>` : ''}
    </div>
  `;
}

// ─── Fix list + checks ─────────────────────────────────────────────────────

export interface FixItem {
  status: 'red' | 'yellow';
  title: string;
  detail: string;
  /** Optional item the fix is about (texture name, extension…). */
  subject?: string;
  /** Optional measured-vs-target line. */
  values?: string;
}

/** "What to fix" — or a single all-clear row when there's nothing. */
export function renderFixSection(title: string, items: FixItem[], allClear: string, lede = ''): string {
  const body = items.length === 0
    ? `<div class="all-clear">${ICONS.check()}<span>${escapeHtml(allClear)}</span></div>`
    : `<ul class="fix-list">${items.map(renderFix).join('')}</ul>`;
  return `
    <section class="section">
      <h2 class="section-title">${escapeHtml(title)}${items.length ? `<span class="count">${items.length}</span>` : ''}</h2>
      ${lede && items.length ? `<p class="section-lede">${lede}</p>` : ''}
      ${body}
    </section>
  `;
}

function renderFix(item: FixItem): string {
  return `
    <li class="fix">
      <span class="fix-mark ${item.status}" aria-label="${item.status === 'red' ? 'Must fix' : 'Should fix'}">${item.status === 'red' ? '!' : '–'}</span>
      <div>
        <div class="fix-title">${escapeHtml(item.title)}${item.subject ? `<span class="fix-subject">${escapeHtml(item.subject)}</span>` : ''}</div>
        <div class="fix-detail">${escapeHtml(item.detail)}</div>
        ${item.values ? `<div class="fix-values">${escapeHtml(item.values)}</div>` : ''}
      </div>
    </li>
  `;
}

/** An axis as every analyzer reports it. */
export interface CheckAxis {
  axis: string;
  status: Status;
  actual: string;
  target: string;
  explanation: string;
}

/** Turn failing axes into fix items, worst first. */
export function axesToFixes(axes: readonly CheckAxis[], labels: Record<string, string>): FixItem[] {
  return axes
    .filter((a): a is CheckAxis & { status: 'red' | 'yellow' } => a.status !== 'green')
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'red' ? -1 : 1))
    .map(a => ({
      status: a.status,
      title: labels[a.axis] ?? a.axis,
      detail: a.explanation,
      values: `${a.actual} (target: ${a.target})`,
    }));
}

/** Every check, passing or not — lives under Details. */
export function renderChecks(axes: readonly CheckAxis[], labels: Record<string, string>): string {
  return `
    <ul class="checks">
      ${axes.map(a => `
        <li class="check">
          <span class="check-dot ${a.status}" aria-label="${a.status === 'green' ? 'Pass' : a.status === 'yellow' ? 'Warning' : 'Fail'}"></span>
          <span class="check-name">${escapeHtml(labels[a.axis] ?? a.axis)}</span>
          <span class="check-values">${escapeHtml(a.actual)} <span class="muted">(target ${escapeHtml(a.target)})</span>
            <span class="check-explain">${escapeHtml(a.explanation)}</span>
          </span>
        </li>`).join('')}
    </ul>
  `;
}

/** One "before → after" line of a change log; says "unchanged" when equal. */
export function renderChangeRow(label: string, before: string, after: string): string {
  const value = before === after
    ? `<span class="same">${escapeHtml(after)}, unchanged</span>`
    : `<span><span class="from">${escapeHtml(before)}</span> → <span class="to">${escapeHtml(after)}</span></span>`;
  return `<div class="change-row"><span class="muted">${escapeHtml(label)}</span>${value}</div>`;
}

/** A plain key/value table. */
export function renderKv(rows: Array<[string, string]>): string {
  return `<dl class="kv">${rows.map(([k, v]) => `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join('')}</dl>`;
}

// ─── Disclosures ───────────────────────────────────────────────────────────

/**
 * Open/closed state for every disclosure, keyed by a stable id. The tools
 * re-render with innerHTML, which would otherwise snap every <details> shut on
 * the next state change. Unset keys fall back to the caller's default.
 */
const openState = new Map<string, boolean>();

export function renderDisclosure(
  key: string,
  title: string,
  body: string,
  opts: { meta?: string; metaTone?: 'accent' | 'warn'; open?: boolean } = {},
): string {
  const open = openState.get(key) ?? opts.open ?? false;
  const meta = opts.meta
    ? `<span class="disclosure-meta${opts.metaTone ? ` ${opts.metaTone}` : ''}">${opts.meta}</span>`
    : '';
  return `
    <details class="disclosure" data-disclosure="${key}" ${open ? 'open' : ''}>
      <summary><span class="disclosure-title">${escapeHtml(title)}</span>${meta}</summary>
      <div class="disclosure-body">${body}</div>
    </details>
  `;
}

/** Remember disclosure toggles. `toggle` doesn't bubble, so listen in capture. */
export function wireDisclosures(root: HTMLElement): void {
  root.addEventListener('toggle', (e) => {
    const el = e.target as HTMLElement;
    if (el instanceof HTMLDetailsElement && el.dataset.disclosure) {
      openState.set(el.dataset.disclosure, el.open);
    }
  }, true);
}

/** Forget remembered disclosure state (e.g. when a new file is loaded). */
export function resetDisclosures(): void {
  openState.clear();
}

// ─── Actions ───────────────────────────────────────────────────────────────

/** Save a Blob under `filename` via a temporary object URL. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Copy text, reflecting the outcome in the button's label for a moment. */
export function copyWithFeedback(button: HTMLButtonElement, text: string): void {
  const original = button.textContent;
  const flash = (label: string, ms: number): void => {
    button.textContent = label;
    setTimeout(() => { button.textContent = original; }, ms);
  };
  navigator.clipboard.writeText(text).then(
    () => flash('Copied', 1200),
    (err: unknown) => {
      // Clipboard fails on insecure contexts and when permission is denied.
      // Say so rather than silently lying.
      flash('Copy failed — select and copy manually', 2400);
      console.warn('Clipboard write failed:', err);
    },
  );
}
